import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, Clock, MapPin } from 'lucide-react';
import { getOutboundCallBaseUrl } from '../lib/apiConfig';

// Calendar endpoints live on the v2 server (port 3017), not the agent server (3016).
// getOutboundCallBaseUrl reliably resolves to the :3017 v2 server (same base outbound calls use).
const API_BASE = getOutboundCallBaseUrl();

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
});

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const toLocalInput = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function CalendarPage() {
  const [cursor, setCursor] = useState(() => new Date());
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // { date, form } for create

  const monthStart = useMemo(() => startOfMonth(cursor), [cursor]);
  const monthEnd = useMemo(() => endOfMonth(cursor), [cursor]);

  const loadMeetings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = `${API_BASE}/api/calendar/meetings?start=${monthStart.toISOString()}&end=${monthEnd.toISOString()}`;
      const res = await fetch(url, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load meetings');
      setMeetings(data.meetings || []);
    } catch (e) {
      setError(e.message);
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [monthStart, monthEnd]);

  useEffect(() => { loadMeetings(); }, [loadMeetings]);

  // Build the 6-week grid (leading/trailing days from adjacent months)
  const grid = useMemo(() => {
    const firstWeekday = monthStart.getDay();
    const cells = [];
    const gridStart = new Date(monthStart);
    gridStart.setDate(gridStart.getDate() - firstWeekday);
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [monthStart]);

  const meetingsForDay = useCallback(
    (day) => meetings.filter((m) => sameDay(new Date(m.start), day)).sort((a, b) => new Date(a.start) - new Date(b.start)),
    [meetings]
  );

  const openCreate = (day) => {
    const start = new Date(day);
    start.setHours(9, 0, 0, 0);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + 30);
    setModal({ id: null, form: { title: '', description: '', location: '', start: toLocalInput(start), end: toLocalInput(end) } });
  };

  // Read an existing meeting into the modal for editing.
  const openEdit = (m) => {
    setModal({
      id: m._id,
      form: {
        title: m.title || '',
        description: m.description || '',
        location: m.location || '',
        start: toLocalInput(new Date(m.start)),
        end: toLocalInput(new Date(m.end)),
      },
    });
  };

  // Create (POST) or update (PUT) depending on whether the modal has an id.
  const saveMeeting = async () => {
    const f = modal.form;
    if (!f.title.trim()) { setError('Title is required'); return; }
    const isEdit = !!modal.id;
    try {
      const res = await fetch(
        `${API_BASE}/api/calendar/meetings${isEdit ? `/${modal.id}` : ''}`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            title: f.title,
            description: f.description,
            location: f.location,
            start: new Date(f.start).toISOString(),
            end: new Date(f.end).toISOString(),
          }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Failed to ${isEdit ? 'update' : 'create'} meeting`);
      setModal(null);
      loadMeetings();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/calendar/meetings/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to delete');
      setModal(null);
      loadMeetings();
    } catch (e) {
      setError(e.message);
    }
  };

  const today = new Date();
  const setField = (k, v) => setModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));

  return (
    <div className="p-4 md:p-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-800">
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </h1>
          <div className="flex items-center gap-1">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-600" aria-label="Previous month">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => setCursor(new Date())}
              className="px-3 py-1.5 text-sm rounded border border-gray-200 hover:bg-gray-50 text-gray-700">Today</button>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-600" aria-label="Next month">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
        <button onClick={() => openCreate(today)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#459EA3] text-white text-sm hover:bg-[#3b888c]">
          <Plus className="w-4 h-4" /> New meeting
        </button>
      </div>

      {error && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {loading && <div className="mb-3 text-sm text-gray-500">Loading…</div>}

      {/* Weekday header */}
      <div className="grid grid-cols-7 text-xs font-medium text-gray-500 border-b border-gray-200">
        {WEEKDAYS.map((w) => <div key={w} className="px-2 py-2 text-center">{w}</div>)}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 border-l border-t border-gray-200">
        {grid.map((day, i) => {
          const inMonth = day.getMonth() === cursor.getMonth();
          const isToday = sameDay(day, today);
          const dayMeetings = meetingsForDay(day);
          return (
            <div key={i}
              onClick={() => openCreate(day)}
              className={`min-h-[96px] border-r border-b border-gray-200 p-1.5 cursor-pointer hover:bg-gray-50 ${inMonth ? 'bg-white' : 'bg-gray-50/60'}`}>
              <div className="flex justify-end">
                <span className={`text-xs w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-[#459EA3] text-white' : inMonth ? 'text-gray-700' : 'text-gray-400'}`}>
                  {day.getDate()}
                </span>
              </div>
              <div className="mt-1 space-y-1">
                {dayMeetings.slice(0, 3).map((m) => (
                  <div key={m._id}
                    onClick={(e) => { e.stopPropagation(); openEdit(m); }}
                    title={`${m.title} — click to edit`}
                    className="text-[11px] leading-tight truncate px-1.5 py-0.5 rounded bg-[#459EA3]/10 text-[#27758a] hover:bg-[#459EA3]/20">
                    {new Date(m.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} {m.title}
                  </div>
                ))}
                {dayMeetings.length > 3 && <div className="text-[10px] text-gray-400 px-1.5">+{dayMeetings.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800">{modal.id ? 'Edit meeting' : 'New meeting'}</h2>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <input autoFocus type="text" placeholder="Title" value={modal.form.title}
                onChange={(e) => setField('title', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-500">Start
                  <input type="datetime-local" value={modal.form.start} onChange={(e) => setField('start', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm mt-1" />
                </label>
                <label className="text-xs text-gray-500">End
                  <input type="datetime-local" value={modal.form.end} onChange={(e) => setField('end', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm mt-1" />
                </label>
              </div>
              <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                <input type="text" placeholder="Location (optional)" value={modal.form.location}
                  onChange={(e) => setField('location', e.target.value)} className="w-full text-sm outline-none" />
              </div>
              <textarea placeholder="Description (optional)" rows={3} value={modal.form.description}
                onChange={(e) => setField('description', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>
            <div className="flex justify-between items-center gap-2 px-5 py-3 border-t border-gray-100">
              <div>
                {modal.id && (
                  <button onClick={() => deleteMeeting(modal.id)}
                    className="px-4 py-2 text-sm rounded-lg text-red-600 hover:bg-red-50">Delete</button>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setModal(null)} className="px-4 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700">Cancel</button>
                <button onClick={saveMeeting} className="px-4 py-2 text-sm rounded-lg bg-[#459EA3] text-white hover:bg-[#3b888c]">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <p className="mt-3 text-xs text-gray-400 flex items-center gap-1">
        <Clock className="w-3 h-3" /> Click any day to add a meeting, or click a meeting to edit or delete it.
      </p>
    </div>
  );
}
